// src/features/patient/care-treatment/components/LoadOlder.tsx

/**
 * Purpose : One paging rule for every Care & Treatment list (visits, calls &
 * letters, care plans, care team, to review): 10 at a time, and a "Load
 * older" button only when there are more than are shown — never under a
 * list of 10 or fewer (2026-10-29: "Load older notes" showed under any list,
 * even an empty one, whenever the server had more pages of notes).
 *
 * Contents :
 *
 * * const(PAGE_SIZE)
 * * function(usePaged) : the first N items, whether there are more, and "more"
 * * function(LoadOlderButton) : the button, centred under the list
 */

import { useState } from "react";
import { Button } from "@/shared/components/button";
import { brandButtonClass } from "./careStyles";

// const(PAGE_SIZE)
export const PAGE_SIZE = 10;

// function(usePaged)
/**
 * @param items - The whole list, in the order shown.
 * @param resetKey - Back to the first 10 when it changes (another filter, tab or view).
 */
export function usePaged<T>(items: T[], resetKey = "") {
  const [state, setState] = useState({ key: resetKey, limit: PAGE_SIZE });
  // Set while rendering (not in an effect), so a new filter never shows the old window first.
  const limit = state.key === resetKey ? state.limit : PAGE_SIZE;
  if (state.key !== resetKey) setState({ key: resetKey, limit: PAGE_SIZE });
  return {
    shown: items.length > limit ? items.slice(0, limit) : items,
    hasMore: items.length > limit,
    more: () => setState({ key: resetKey, limit: limit + PAGE_SIZE }),
  };
}

// function(LoadOlderButton)
/** "Load older" (or `label`), centred; renders nothing when `show` is false. */
export function LoadOlderButton({ show, onClick, busy = false, label = "Load older" }: {
  show: boolean;
  onClick: () => void;
  busy?: boolean;
  label?: string;
}) {
  if (!show) return null;
  return (
    <div className="mt-2 flex justify-center">
      <Button onClick={onClick} busy={busy} className={brandButtonClass}>{label}</Button>
    </div>
  );
}
